/**
 * Who may approve a pending change set, as the register publishes it — bata-ui ADR 2005.
 *
 * Since platform v0.50 every `ChangeSetSummary` carries `approval`: whether THIS caller may approve
 * it (`callerMayApprove`) and, when not, why (`callerBlockedBy`: `author`, `role` or `state`), and
 * the realm roles the approval route grants. The queue renders that answer instead of a button the
 * route would refuse:
 *
 *   * `author` — the caller proposed it and its category rule forbids self-approval: "waiting for
 *     another approver", naming who can approve. No rule forbids it today: the owner ruling of
 *     2026-10-09 retired the reviewer ≠ author overlay for every family (bata-platform ADR 9026),
 *     so the author of a proposal may approve it. The state stays because the contract keeps it.
 *   * `role` — the caller's roles do not hold the approval grant: who does.
 *   * `state` — no longer pending: nothing to offer.
 *
 * A platform before v0.50 publishes no `approval`; the queue then offers Approve as it always did
 * and the platform's refusal is the judge (ADR 2003). Pure: the tests assert values.
 */

import type { ChangeSetSummary } from '@/api/generated';

export type ApprovalStanding =
  /** No `approval` published (platform before v0.50): offer Approve; the platform judges. */
  | { readonly kind: 'unpublished' }
  | { readonly kind: 'mayApprove' }
  | { readonly kind: 'awaitingAnother'; readonly approverRoles: readonly string[] }
  | { readonly kind: 'notYourRole'; readonly approverRoles: readonly string[] }
  | { readonly kind: 'notPending' }
  /** A reason word this build does not know, verbatim — never mapped onto a known one. */
  | { readonly kind: 'blocked'; readonly blockedBy: string; readonly approverRoles: readonly string[] };

export function approvalStanding(change: ChangeSetSummary): ApprovalStanding {
  const approval = change.approval;
  if (approval === null || approval === undefined) return { kind: 'unpublished' };
  const approverRoles = approval.approverRoles ?? [];
  if (approval.callerMayApprove) return { kind: 'mayApprove' };
  const blockedBy = approval.callerBlockedBy ?? null;
  if (blockedBy === 'author') return { kind: 'awaitingAnother', approverRoles };
  if (blockedBy === 'role') return { kind: 'notYourRole', approverRoles };
  if (blockedBy === 'state') return { kind: 'notPending' };
  return { kind: 'blocked', blockedBy: blockedBy ?? 'unstated', approverRoles };
}

/** Whether the queue offers Approve on this row. */
export function offersApprove(change: ChangeSetSummary): boolean {
  if (change.state !== 'proposed') return false;
  const standing = approvalStanding(change);
  return standing.kind === 'unpublished' || standing.kind === 'mayApprove';
}

/** Each parameter family's name, spelled out for the catalogue check (the change record's filter). */
const FAMILY_KEY: Readonly<Record<string, string>> = {
  inseason: 'foundations.approvals.family.inseason',
  allocation: 'foundations.approvals.family.allocation',
  markdown: 'foundations.approvals.family.markdown',
  exit: 'foundations.approvals.family.exit',
  elasticity: 'foundations.approvals.family.elasticity',
  module: 'foundations.approvals.family.module',
  // bata-platform ADR 2136 (ADR 9068): a process-calendar set's items are `process_calendar.<country>.<period>`.
  process_calendar: 'foundations.approvals.family.processCalendar',
};

/** A family's catalogue key, or `null` for a family this build has no name for (shown as itself). */
export function familyKey(family: string): string | null {
  return FAMILY_KEY[family] ?? null;
}
